import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Search as SearchIcon } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/api";
import { Seo } from "@/components/Seo";
import { MaskedLine } from "@/components/Motion";
import { WatermarkOverlay } from "@/components/WatermarkOverlay";
import { Input } from "@/components/ui/input";
import { useCart } from "@/context/CartContext";
import { formatPrice, slugifyName } from "@/lib/utils";

const filters = [
  { label: "All machines", value: "all" },
  { label: "Motorcycles", value: "motorcycles" },
  { label: "Cars", value: "cars" },
  { label: "Both", value: "both" },
];

export default function Search() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      api.get("/search/photos", {
        params: {
          q: query || undefined,
          category,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
      })
        .then((response) => setResults(response.data))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [query, category, dateFrom, dateTo]);

  return (
    <div data-testid="search-page" className="min-h-screen bg-onyx pt-32">
      <Seo title="Search the Archive" description="Search mellowjmedia frames by bike, car, rider, driver, event, location, and date." />
      <section className="mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12">
        <p className="font-mono text-xs uppercase tracking-[0.42em] text-gold">Archive search</p>
        <h1 className="mt-6 font-display text-5xl font-light leading-none text-white sm:text-7xl">
          <MaskedLine>Find your</MaskedLine>
          <MaskedLine delay={0.14}><span className="italic text-titanium">machine.</span></MaskedLine>
        </h1>
        <div className="relative mt-12 max-w-3xl">
          <SearchIcon className="absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-gold/70" />
          <Input
            data-testid="search-query-input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Bike, car, rider, event, location or camera detail"
            className="h-16 rounded-none border-white/15 bg-white/[0.03] pl-14 text-lg text-white placeholder:text-zinc-600 focus-visible:ring-gold"
          />
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          {filters.map((filter) => (
            <button key={filter.value} data-testid={`search-filter-${filter.value}`} onClick={() => setCategory(filter.value)} className={`border px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors ${category === filter.value ? "border-gold bg-gold text-onyx" : "border-white/10 text-zinc-400 hover:border-gold/50 hover:text-white"}`}>
              {filter.label}
            </button>
          ))}
          <div className="ml-auto flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              From
              <Input data-testid="search-date-from" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className="h-11 w-40 rounded-none border-white/10 bg-white/[0.03] text-white" />
            </label>
            <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              To
              <Input data-testid="search-date-to" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} className="h-11 w-40 rounded-none border-white/10 bg-white/[0.03] text-white" />
            </label>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-4 py-16 sm:px-8 lg:px-12">
        <div className="mb-8 flex items-center justify-between border-b border-white/10 pb-5">
          <p data-testid="search-results-count" className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
            {loading ? "Searching the archive" : `${results.length} frame${results.length === 1 ? "" : "s"} found`}
          </p>
          <Link data-testid="search-browse-events-link" to="/events" className="font-mono text-xs uppercase tracking-[0.24em] text-gold hover:text-titanium">Browse by event</Link>
        </div>
        {!loading && results.length === 0 && (
          <div data-testid="search-empty-state" className="border border-white/10 bg-carbon p-12 text-center">
            <p className="font-display text-3xl text-white">No frames match that search.</p>
            <p className="mt-3 text-sm text-zinc-500">Try a bike model, rider name, event, city, or a broader date range.</p>
          </div>
        )}
        <div className="columns-1 gap-5 space-y-5 sm:columns-2 lg:columns-3 xl:columns-4">
          {results.map((photo, index) => (
            <motion.article key={photo.id} data-testid={`search-result-${photo.id}`} initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.04, 0.5), duration: 0.55 }} className="group relative break-inside-avoid overflow-hidden border border-white/10 bg-carbon">
              <Link data-testid={`search-result-gallery-link-${photo.id}`} to={`/events/${photo.event_slug}`} className="relative block">
                <img src={photo.preview_url} alt={`${photo.title} from ${photo.event_title}`} loading="lazy" className="w-full object-cover transition duration-700 group-hover:scale-[1.025]" />
                <WatermarkOverlay />
              </Link>
              <div className="p-5">
                <h3 className="font-display text-2xl text-white">{photo.title}</h3>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                  {photo.vehicle || ""}
                  {photo.rider && <>{photo.vehicle ? " · " : ""}<Link data-testid={`search-rider-link-${photo.id}`} to={`/riders/${slugifyName(photo.rider)}`} className="text-gold transition-colors hover:text-titanium">{photo.rider}</Link></>}
                  {!photo.vehicle && !photo.rider && "Event frame"}
                </p>
                <Link data-testid={`search-result-event-${photo.id}`} to={`/events/${photo.event_slug}`} className="mt-4 block font-lux text-sm uppercase tracking-[0.16em] text-gold hover:text-titanium">{photo.event_title}</Link>
                <div className="mt-3 flex flex-wrap gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{photo.event_location}</span>
                  <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{photo.event_date}</span>
                </div>
                <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-4">
                  <span className="font-display text-2xl text-white">${formatPrice(photo.price)}</span>
                  <button data-testid={`search-buy-photo-${photo.id}`} onClick={() => addItem({ item_type: "photo", item_id: photo.id, title: photo.title, price: photo.price })} className="border border-gold/35 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-gold transition-colors hover:bg-gold hover:text-onyx">
                    Buy frame
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
